import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, TrendingUp, Calculator, Info, Sparkles, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { API } from "../../lib/helpers";

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
const fmtMoneyExact = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—";
};
const fmtPct = (v, digits = 1) => {
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(digits)}%` : "—";
};

// ─────────────────────────────────────────────────────────────────────
// Tier 1 — Lender-Grade Metrics (free for everyone)
// ─────────────────────────────────────────────────────────────────────
const Metric = ({ label, value, status, hint, testid }) => {
  const tone = {
    good:    "border-emerald-500/40 bg-emerald-500/5  text-emerald-400",
    warn:    "border-amber-500/40   bg-amber-500/5    text-amber-400",
    bad:     "border-red-500/40     bg-red-500/5      text-red-400",
    neutral: "border-border         bg-secondary/40   text-foreground",
  }[status || "neutral"];
  return (
    <div className={`p-3 rounded-lg border ${tone}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-1">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 opacity-50 hover:opacity-100 cursor-help flex-shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <p className="font-mono text-base font-bold mt-1">{value}</p>
    </div>
  );
};

const HealthBadge = ({ ok, label, hint }) => (
  <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
    ok ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
       : "bg-red-500/10 text-red-400 border border-red-500/30"
  }`} title={hint}>
    {ok ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    <span className="font-medium">{label}</span>
    <span className="opacity-70">{ok ? "Pass" : "Fail"}</span>
  </div>
);

export const LenderGradeMetrics = ({ analysis }) => {
  if (!analysis) return null;
  const dscr = analysis.dscr;
  const debtYield = analysis.debt_yield;
  const breakeven = analysis.breakeven_occupancy;
  const grm = analysis.gross_rent_multiplier;

  const dscrStatus = dscr == null ? "neutral" : dscr >= 1.25 ? "good" : dscr >= 1.0 ? "warn" : "bad";
  const dyStatus   = debtYield == null ? "neutral" : debtYield >= 10 ? "good" : debtYield >= 8 ? "warn" : "bad";
  const beStatus   = breakeven == null ? "neutral" : breakeven <= 75 ? "good" : breakeven <= 90 ? "warn" : "bad";
  const grmStatus  = grm == null ? "neutral" : grm <= 8 ? "good" : grm <= 12 ? "warn" : "bad";

  return (
    <Card className="bg-card border-border" data-testid="lender-grade-metrics">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-semibold">Lender-Grade Metrics</h3>
          <span className="text-[10px] text-muted-foreground">— what your bank actually checks</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Metric
            label="DSCR"
            value={dscr == null ? "—" : `${dscr.toFixed(2)}x`}
            status={dscrStatus}
            testid="metric-dscr"
            hint="Debt Service Coverage Ratio = NOI ÷ Annual Debt Service. Lenders want 1.20× or higher. Below 1.0× means the property's income doesn't cover the mortgage."
          />
          <Metric
            label="Debt Yield"
            value={fmtPct(debtYield)}
            status={dyStatus}
            testid="metric-debt-yield"
            hint="NOI ÷ Loan Amount. Tells lenders how risky the loan is. 10%+ is healthy, below 8% is hard to finance."
          />
          <Metric
            label="Break-Even Occupancy"
            value={fmtPct(breakeven)}
            status={beStatus}
            testid="metric-breakeven"
            hint="The minimum occupancy you need to cover ALL costs (operating expenses + mortgage). Below this %, you lose money. Lower is safer."
          />
          <Metric
            label="GRM"
            value={grm == null ? "—" : `${grm.toFixed(2)}`}
            status={grmStatus}
            testid="metric-grm"
            hint="Gross Rent Multiplier = Price ÷ Annual Gross Rent. Quick value yardstick. Below 8 is undervalued, above 12 is expensive."
          />
        </div>

        {/* Pass/Fail health check badges */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border/50">
          <span className="text-[10px] text-muted-foreground self-center mr-1">Health checks:</span>
          <HealthBadge
            ok={Number.isFinite(Number(analysis.cap_rate)) && Number(analysis.cap_rate) >= 6}
            label="Cap Rate ≥ 6%"
            hint="Above 6% cap rate means competitive yield for residential rentals."
          />
          <HealthBadge
            ok={Number.isFinite(Number(analysis.cash_on_cash_return)) && Number(analysis.cash_on_cash_return) >= 8}
            label="CoC ≥ 8%"
            hint="Above 8% Cash-on-Cash means your invested capital is working hard."
          />
          <HealthBadge
            ok={dscr != null && dscr >= 1.2}
            label="DSCR ≥ 1.20"
            hint="Lender minimum for most investment loans."
          />
          <HealthBadge
            ok={!!analysis.one_percent_rule}
            label="1% Rule"
            hint="Monthly rent ≥ 1% of purchase price."
          />
          <HealthBadge
            ok={!!analysis.fifty_percent_rule}
            label="50% Rule"
            hint="Operating expenses ≤ 50% of gross income."
          />
        </div>
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────────────────────────────────────────────
// Tier 2 — 10-Year Investment Projection (Elite-only)
// ─────────────────────────────────────────────────────────────────────
export const TenYearProjection = ({ propertyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .post(`${API}/properties/${propertyId}/projection`, {})
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Could not load projection"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-4 text-xs text-muted-foreground">Loading 10-year projection…</CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return null; // silently skip if API fails — non-critical UI
  }

  // ─── Locked preview for Pro/Free ───
  if (data.locked) {
    const t = data.teaser || {};
    return (
      <Card className="bg-gradient-to-br from-amber-500/5 to-transparent border-amber-500/30" data-testid="projection-locked">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold">10-Year Investment Projection</h3>
                <Lock className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                See your year-by-year wealth build, IRR, and equity multiple — Elite feature.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/billing?tier=elite_monthly&src=projection_locked")}
              data-testid="projection-upgrade-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              Unlock with Elite
            </Button>
          </div>

          {/* Blurred teaser numbers */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 relative">
            <div className="p-3 rounded-lg border border-border bg-secondary/40">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Return ({t.hold_years}y)</p>
              <p className="font-mono text-base font-bold blur-sm select-none">{fmtMoney(t.total_return)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-secondary/40">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">IRR</p>
              <p className="font-mono text-base font-bold blur-sm select-none">{fmtPct(t.irr_pct)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-secondary/40">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Equity Multiple</p>
              <p className="font-mono text-base font-bold blur-sm select-none">
                {t.equity_multiple == null ? "—" : `${t.equity_multiple}x`}
              </p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-secondary/40">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Sale Price (Year 10)</p>
              <p className="font-mono text-base font-bold blur-sm select-none">{fmtMoney(t.estimated_sale_price)}</p>
            </div>
          </div>
          <p className="text-[10px] text-amber-500/80 text-center mt-3">
            Projected with 3% rent growth, 2.5% expense growth, 3.5% appreciation
          </p>
        </CardContent>
      </Card>
    );
  }

  // ─── Full Elite view ───
  const s = data.summary || {};
  const years = data.years || [];

  return (
    <Card className="bg-card border-border" data-testid="projection-unlocked">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold">10-Year Investment Projection</h3>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-500 border border-amber-500/30">ELITE</span>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Metric
            label="Total Return"
            value={fmtMoney(s.total_return)}
            status={s.total_return > 0 ? "good" : "bad"}
            testid="proj-total-return"
            hint="Cash flow collected over 10 years + net sale proceeds, minus your initial cash invested. The bottom line."
          />
          <Metric
            label="IRR"
            value={fmtPct(s.irr_pct)}
            status={s.irr_pct >= 12 ? "good" : s.irr_pct >= 8 ? "warn" : "bad"}
            testid="proj-irr"
            hint="Internal Rate of Return = your annualized return over the hold period. 12%+ is excellent, 8-12% is solid, below 8% is mediocre."
          />
          <Metric
            label="Equity Multiple"
            value={s.equity_multiple == null ? "—" : `${s.equity_multiple}x`}
            status={s.equity_multiple >= 2 ? "good" : s.equity_multiple >= 1.5 ? "warn" : "bad"}
            testid="proj-eqm"
            hint="Total cash returned ÷ initial cash invested. 2.0x means you doubled your money. Above 1.5x is healthy."
          />
          <Metric
            label="Avg Annual Cash Flow"
            value={fmtMoney(s.avg_annual_cash_flow)}
            status={s.avg_annual_cash_flow > 0 ? "good" : "bad"}
            testid="proj-avg-cf"
            hint="Average yearly cash flow over the 10-year hold, after rent growth and expense growth."
          />
        </div>

        {/* Year-by-year table */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs font-mono">
            <thead className="bg-secondary/60">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-2 py-1.5">Year</th>
                <th className="text-right px-2 py-1.5">Cash Flow</th>
                <th className="text-right px-2 py-1.5">Principal Paid</th>
                <th className="text-right px-2 py-1.5">Property Value</th>
                <th className="text-right px-2 py-1.5">Equity</th>
              </tr>
            </thead>
            <tbody>
              {years.map((y) => (
                <tr key={y.year} className="border-t border-border/50">
                  <td className="px-2 py-1.5 text-muted-foreground">Year {y.year}</td>
                  <td className={`px-2 py-1.5 text-right ${y.cash_flow >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {fmtMoneyExact(y.cash_flow)}
                  </td>
                  <td className="px-2 py-1.5 text-right text-foreground">{fmtMoneyExact(y.principal_paid)}</td>
                  <td className="px-2 py-1.5 text-right text-foreground">{fmtMoneyExact(y.property_value)}</td>
                  <td className="px-2 py-1.5 text-right text-emerald-400 font-semibold">{fmtMoneyExact(y.equity)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-emerald-500/5 border-t border-emerald-500/30">
              <tr>
                <td className="px-2 py-1.5 text-emerald-400 font-semibold">Exit (Sale)</td>
                <td className="px-2 py-1.5 text-right text-muted-foreground">—</td>
                <td className="px-2 py-1.5 text-right text-muted-foreground">—</td>
                <td className="px-2 py-1.5 text-right text-foreground">{fmtMoneyExact(s.estimated_sale_price)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-400 font-bold">
                  +{fmtMoneyExact(s.net_sale_proceeds)} net
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="text-[10px] text-muted-foreground leading-relaxed">
          Assumptions: {s.assumptions?.rent_growth_pct}% rent growth · {s.assumptions?.expense_growth_pct}% expense growth · {s.assumptions?.appreciation_pct}% appreciation · {s.assumptions?.selling_cost_pct}% selling cost
        </div>
      </CardContent>
    </Card>
  );
};
